<template>
    <v-container class="py-7">
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">{{ meeting ? meeting.name : 'Assembleia' }}</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbItems"></v-breadcrumbs>
                    </div>
                    <div class="button-group">
                        <v-btn v-if="meeting && canEdit" elevation="0" color="primary" outlined :to="`/agcs/${meeting.id}/editar`">
                            <v-icon left small>mdi-pencil-outline</v-icon> Editar
                        </v-btn>
                        <v-btn elevation="0" color="primary" outlined to="/agcs">Voltar</v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-row v-if="loading && !meeting">
            <v-col class="text-center py-12">
                <v-progress-circular :size="56" :width="5" color="primary" indeterminate></v-progress-circular>
            </v-col>
        </v-row>

        <template v-if="meeting">
            <v-row>
                <v-col cols="12" sm="4">
                    <v-card class="card-form-outlined agc-kpi">
                        <span class="agc-kpi__value">{{ analytics.total_accreditations || 0 }}</span>
                        <span class="agc-kpi__label">Credenciados</span>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="4">
                    <v-card class="card-form-outlined agc-kpi">
                        <span class="agc-kpi__value">R$ {{ (analytics.total_amount || 0) | currency }}</span>
                        <span class="agc-kpi__label">Crédito credenciado</span>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="4">
                    <v-card class="card-form-outlined agc-kpi">
                        <span class="agc-kpi__value">{{ analytics.total_amount_percentage || 0 }}%</span>
                        <span class="agc-kpi__label">Do crédito total do caso</span>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <!-- Dados da assembleia -->
                <v-col cols="12" lg="8">
                    <v-card class="card-form-outlined agc-section">
                        <div class="agc-section__title">Dados da assembleia</div>
                        <div class="agc-info">
                            <div class="agc-info__item">
                                <span class="agc-info__label">Caso</span>
                                <span class="agc-info__value">{{ meeting.client ? meeting.client.name : '—' }}</span>
                            </div>
                            <div class="agc-info__item">
                                <span class="agc-info__label">Data e hora</span>
                                <span class="agc-info__value">{{ meeting.date | defaultDate('DD/MM/YYYY HH:mm') }}</span>
                            </div>
                            <div class="agc-info__item agc-info__item--wide">
                                <span class="agc-info__label">Descrição</span>
                                <div class="agc-info__value agc-description" v-html="meeting.description || '—'"></div>
                            </div>
                            <div v-if="meeting.comments" class="agc-info__item agc-info__item--wide">
                                <span class="agc-info__label">Informações adicionais</span>
                                <div class="agc-info__value agc-description" v-html="meeting.comments"></div>
                            </div>
                            <div class="agc-info__item agc-info__item--wide">
                                <span class="agc-info__label">Edital</span>
                                <a v-if="meeting.publicNoticeDocUrl" class="agc-link" :href="meeting.publicNoticeDocUrl"
                                    target="_blank" rel="noopener">
                                    <v-icon small color="primary">mdi-file-document-outline</v-icon> Abrir edital
                                </a>
                                <span v-else class="agc-info__value">Nenhum edital enviado.</span>
                            </div>
                        </div>
                    </v-card>
                </v-col>

                <!-- Situação e ações -->
                <v-col cols="12" lg="4">
                    <v-card class="card-form-outlined agc-section">
                        <div class="agc-section__title">Situação</div>
                        <v-chip label text-color="white" :color="statusColor(meeting.status)" class="mb-4">
                            {{ statusName(meeting.status) }}
                        </v-chip>

                        <div class="agc-deadline">
                            <div class="d-flex justify-space-between mb-1">
                                <span class="agc-info__label">Prazo para credenciamento</span>
                                <strong class="agc-deadline__days">
                                    {{ deadlineLabel }}
                                </strong>
                            </div>
                            <v-progress-linear :value="meeting.totalDiffPercentage" height="6" rounded
                                background-color="lightGray" :color="deadlineColor(meeting.totalDiffPercentage)" />
                        </div>

                        <template v-if="actions.length">
                            <v-divider class="my-4"></v-divider>
                            <v-btn v-for="action in actions" :key="action.label" block elevation="0" class="mb-2"
                                :color="action.color || 'primary'" :outlined="!action.primary" @click="runAction(action.key)">
                                <v-icon left small>{{ action.icon }}</v-icon> {{ action.label }}
                            </v-btn>
                        </template>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card class="table-card">
                        <v-card-title class="table-card-title d-flex justify-space-between">
                            Credenciamentos
                            <v-btn small outlined color="primary" elevation="0" @click="generateCSV()">
                                <v-icon left small>mdi-microsoft-excel</v-icon> Exportar CSV
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <v-data-table :headers="accreditations.headers" :items="accreditations.data"
                                :server-items-length="accreditations.count" :disable-sort="true"
                                :loading="accreditations.loading" :options.sync="accreditations.options"
                                :footer-props="accreditations.footerOptions"
                                no-data-text="Nenhum credenciamento foi encontrado" loading-text="Carregando dados..."
                                class="app-table hover-table" @click:row="viewAccreditation">

                                <template v-slot:[`item.accreditation.user.name`]="{ item }">
                                    <span class="text-capitalize">{{ String(item.accreditation.user.name || '').toLowerCase() }}</span>
                                </template>

                                <template v-slot:[`item.procurator_name`]="{ item }">
                                    <span class="text-capitalize">{{ item.procurator_name ? item.procurator_name.toLowerCase() : '—' }}</span>
                                </template>

                                <template v-slot:[`item.created_at`]="{ item }">
                                    {{ item.created_at | defaultDate('DD/MM/YYYY HH:mm') }}
                                </template>

                                <template v-slot:[`item.status`]="{ item }">
                                    <v-chip small label text-color="white" :color="accreditationColor(item.status)">
                                        {{ item.status | enum(accreditationStatus, 'id', 'name') }}
                                    </v-chip>
                                </template>

                                <template v-slot:[`item.actions`]="{ item }">
                                    <v-icon size="20" @click.stop="viewAccreditation(item)">mdi-eye-outline</v-icon>
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </template>

        <general-meeting-notice-file-upload ref="fileUploadDialogRef"></general-meeting-notice-file-upload>
        <accreditation-details-modal ref="accreditationDetailsModalRef"></accreditation-details-modal>
        <general-meeting-comments-modal ref="generalMeetingCommentsModalRef"></general-meeting-comments-modal>
    </v-container>
</template>

<script>
import axios from 'axios';

import AccreditationDetailsModal from "@/components/AccreditationDetailsModal.vue"
import GeneralMeetingCommentsModal from "@/components/GeneralMeetingCommentsModal.vue"
import GeneralMeetingNoticeFileUpload from '@/components/GeneralMeetingNoticeFileUpload.vue';
import accreditationStatus from "@/common/data/accreditation-status.data";

// Situação da assembleia (status) e os tipos de evento enviados à API em cada ação
const STATUS = {
    0: { name: 'Aberta', color: 'primary' },
    1: { name: 'Fechada', color: 'primary' },
    2: { name: 'Interrompida', color: 'error' },
    3: { name: 'Suspensa', color: 'warning' },
    4: { name: 'Finalizada', color: 'grey' },
    5: { name: 'Em andamento', color: 'success' },
};

export default {
    name: "GeneralMeetingView",

    components: {
        'accreditation-details-modal': AccreditationDetailsModal,
        'general-meeting-comments-modal': GeneralMeetingCommentsModal,
        'general-meeting-notice-file-upload': GeneralMeetingNoticeFileUpload
    },

    data: () => ({
        loading: false,
        meeting: null,
        analytics: { total_accreditations: 0, total_amount: 0, total_amount_percentage: 0 },
        accreditationStatus,
        accreditations: {
            data: [],
            count: 0,
            loading: false,
            headers: [
                { text: "Credor", value: "accreditation.user.name" },
                { text: "Procurador", value: "procurator_name" },
                { text: "Solicitado em", value: "created_at" },
                { text: "Situação", value: "status" },
                { text: '', value: 'actions', sortable: false, align: 'end' },
            ],
            options: { page: 1, itemsPerPage: 10 },
            footerOptions: {
                itemsPerPageText: 'Itens por página:',
                itemsPerPageOptions: [10, 20, 30, 40, 50],
                pageText: '{0}-{1} de {2}',
            },
        },
    }),

    computed: {
        breadcrumbItems() {
            return [
                { text: 'Início', to: '/home', exact: true },
                { text: 'Assembleias', to: '/agcs', exact: true },
                { text: 'Detalhes', disabled: true },
            ];
        },

        deadlineLabel() {
            if (!this.meeting || this.meeting.totalDiffPercentage >= 100) return 'Encerrado';
            const days = Number(this.meeting.totalDiff || 0);
            return `${days} ${days === 1 ? 'dia' : 'dias'}`;
        },

        canEdit() {
            return this.meeting && (this.meeting.status === 0 || this.meeting.status === 1);
        },

        // Ações disponíveis conforme a situação (mesmas regras da tela anterior)
        actions() {
            const m = this.meeting;
            if (!m || m.status === 2 || m.status === 4) return [];

            const list = [];

            if (m.status === 1 || m.status === 0)
                list.push({ label: 'Iniciar assembleia', icon: 'mdi-play-circle-outline', primary: true, key: 'start' });
            if (m.status === 5) {
                list.push({ label: 'Votação', icon: 'mdi-vote-outline', primary: true, key: 'voting' });
                list.push({ label: 'Finalizar assembleia', icon: 'mdi-check-circle-outline', key: 'finish' });
            }
            if (m.status === 3 || m.status === 1)
                list.push({ label: 'Reabrir', icon: 'mdi-reload', key: 'reopen' });
            if (m.status === 0 || m.status === 1) {
                list.push({ label: 'Adicionar edital', icon: 'mdi-file-document-plus-outline', key: 'notice' });
                list.push({ label: 'Adicionar informações', icon: 'mdi-file-document-edit-outline', key: 'comments' });
            }
            if (m.status !== 3) {
                list.push({ label: 'Suspender', icon: 'mdi-pause-circle-outline', color: 'warning', key: 'suspend' });
                list.push({ label: 'Interromper', icon: 'mdi-close-circle-outline', color: 'error', key: 'interrupt' });
            }

            return list;
        },
    },

    watch: {
        'accreditations.options': {
            handler() {
                if (this.meeting) this.loadAccreditations();
            },
            deep: true,
        },
    },

    created() {
        this.load();
    },

    methods: {
        statusName(status) {
            return (STATUS[status] || { name: '—' }).name;
        },

        statusColor(status) {
            return (STATUS[status] || { color: 'grey' }).color;
        },

        accreditationColor(status) {
            return { 0: 'warning', 1: 'success', 2: 'error' }[status] || 'grey';
        },

        deadlineColor(percentage) {
            if (percentage >= 80) return 'error';
            if (percentage >= 50) return 'warning';
            return 'primary';
        },

        notifyError(error) {
            this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
        },

        async load() {
            this.loading = true;

            try {
                const id = this.$route.params.id;
                const [meeting, analytics] = await Promise.all([
                    axios.get(`/api/admin/general-meetings/${id}`),
                    axios.get(`/api/admin/general-meetings/analytics/${id}`),
                ]);

                this.meeting = meeting.data.content;
                this.analytics = analytics.data.content || this.analytics;

                await this.loadAccreditations();
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.loading = false;
            }
        },

        async loadAccreditations() {
            this.accreditations.loading = true;

            try {
                const result = await axios.get('/api/admin/accreditations', {
                    params: {
                        page: this.accreditations.options.page,
                        limit: this.accreditations.options.itemsPerPage,
                        search: JSON.stringify({ general_meeting_id: this.meeting.id }),
                    },
                });

                this.accreditations.data = result.data.content.data;
                this.accreditations.count = result.data.content.paging.total;
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.accreditations.loading = false;
            }
        },

        runAction(key) {
            const handlers = {
                start: () => this.action(8, 5),
                voting: () => this.$router.push(`/agcs/votacao/${this.meeting.id}`),
                finish: () => this.action(9, 4),
                reopen: () => this.action(1, 0),
                notice: () => this.openNotice(),
                comments: () => this.openComments(),
                suspend: () => this.action(3, 3),
                interrupt: () => this.action(7, 2),
            };

            if (handlers[key]) handlers[key]();
        },

        async action(type, status) {
            const payload = { type, status };

            if (type === 1) {
                const date = await this.$dialog.prompt({
                    text: 'Informe a nova data da assembleia',
                    title: 'Reabrir assembleia',
                    textField: { type: 'datetime-local' },
                    actions: { false: 'Cancelar', true: 'Confirmar' },
                });

                if (!date) {
                    this.$dialog.notify.error('Selecione uma data para continuar!', { position: 'top-right', timeout: 2000 });
                    return;
                }

                payload.date = date;
            }

            const messages = {
                3: 'Deseja suspender a assembleia?',
                7: 'Deseja interromper a assembleia?',
                1: 'Deseja abrir a assembleia?',
                8: 'Deseja iniciar a assembleia?',
                9: 'Deseja finalizar a assembleia?',
            };

            const confirmed = await this.$dialog.confirm({
                text: messages[type] || 'Deseja atualizar a assembleia?',
                title: 'Atenção',
                actions: { false: 'Cancelar', true: 'Confirmar' },
            });
            if (!confirmed) return;

            try {
                await axios.patch(`/api/admin/general-meetings/action/${this.meeting.id}`, payload);
                this.$dialog.notify.info('Assembleia atualizada com sucesso', { position: 'top-right', timeout: 2000 });
                this.load();
            } catch (error) {
                this.notifyError(error);
            }
        },

        openNotice() {
            this.$refs.fileUploadDialogRef.setData(this.meeting);
            this.$refs.fileUploadDialogRef.open().then(() => this.load());
        },

        openComments() {
            this.$refs.generalMeetingCommentsModalRef.setData(this.meeting);
            this.$refs.generalMeetingCommentsModalRef.open().then(() => this.load());
        },

        viewAccreditation(item) {
            this.$refs.accreditationDetailsModalRef.setData(item);
            this.$refs.accreditationDetailsModalRef.open().then(() => this.load());
        },

        async generateCSV() {
            const data = this.accreditations.data;

            if (!data.length) {
                this.$dialog.notify.error('Sem dados para gerar CSV!', { position: 'top-right', timeout: 2000 });
                return;
            }

            const rows = data.map((item) => ({
                "NOME CREDOR": item.accreditation.user.name,
                "EMAIL CREDOR": item.accreditation.user.email,
                "NOME PROCURADOR": item.procurator_name || "N/A",
                "EMAIL PROCURADOR": item.procurator_email || "N/A",
                "SOLICITADO EM": item.created_at,
                "STATUS": item.status === 0 ? "Pendente" : (item.status === 1 ? "Aprovado" : "Rejeitado"),
            }));

            const header = Object.keys(rows[0]);
            const csv = [header.join(';'), ...rows.map((row) => header.map((key) => row[key]).join(';'))].join('\r\n');

            const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `${this.meeting.client ? this.meeting.client.name : 'credenciamentos'}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
    },
};
</script>

<style lang="scss" scoped>
.agc-kpi {
    display: flex;
    flex-direction: column;
    gap: 2px;
    height: 100%;
    padding: 16px 20px;
}

.agc-kpi__value {
    font-size: 24px;
    font-weight: 700;
    color: var(--v-primary-base);
}

.agc-kpi__label {
    font-size: 13px;
    color: var(--v-muted-base);
}

.agc-section {
    height: 100%;
    padding: 20px 24px;
}

.agc-section__title {
    margin-bottom: 14px;
    font-size: 16px;
    font-weight: 700;
    color: var(--v-primary-base);
}

.agc-info {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px 24px;
}

.agc-info__item {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.agc-info__item--wide {
    grid-column: span 2;
}

.agc-info__label {
    font-size: 12px;
    color: var(--v-muted-base);
}

.agc-info__value {
    font-size: 15px;
    color: var(--v-ink-base);
}

.agc-description ::v-deep p {
    margin-bottom: 6px;
}

.agc-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    text-decoration: none;
}

.agc-deadline__days {
    font-size: 13px;
    color: var(--v-primary-base);
}

@media (max-width: 600px) {
    .agc-info {
        grid-template-columns: 1fr;
    }

    .agc-info__item--wide {
        grid-column: span 1;
    }
}
</style>
